<template>
  <div>
    <div class="max-w-4xl md:w-7/8 mx-auto px-8 md:px-20">

      <div v-if="getKeyVisualURL" class="thumbnail mt-16 md:hidden" :style="{'background-image' : `url(${getKeyVisualURL})`}">
        <a v-if="article.youtubeId" :href="`https://youtu.be/${article.youtubeId}`" class="video-thumb absolute top-0 left-0 w-full h-full" @click.exact.prevent="onModalYT(article.youtubeId)" />
      </div>

      <h1 class="text-2xl font-semibold mt-8">{{article.title}}</h1>

      <div class="my-6">
        <categories :categories="[...article.jobs, ...article.issues]" />
      </div>

      <div class="flex items-center">
        <div class="text-sm leading-none">{{article.client}}</div>
        <div class="ml-auto text-xs">{{article.publishedAt}}</div>
      </div>

      <div class="mt-10 text-right lg:hidden">
        <div class="px-2 bg-white rounded shadow inline-flex ml-auto">
          <client-only placeholder="Loading...">
            <share-buttons :article="article" />
          </client-only>
        </div>
      </div>

      <hr class="border-gray-400 mt-10 md:hidden">
    </div>

    <div class="bg-secondary md:py-8 md:pb-16 mt-8 md:mt-24">
      <div class="max-w-4xl md:w-7/8 mx-auto">
        <contents-container>
          <template v-slot:sticky>
            <div class="py-2 w-12 bg-white rounded hidden lg:block shadow-lg">
              <client-only placeholder="Loading...">
                <share-buttons :article="article" />
              </client-only>
            </div>
          </template>

          <div v-if="getKeyVisualURL" class="thumbnail rounded-t -mt-24 hidden md:block" :style="{'background-image' : `url(${getKeyVisualURL})`}">
            <a v-if="article.youtubeId" :href="`https://youtu.be/${article.youtubeId}`" class="video-thumb absolute top-0 left-0 w-full h-full" @click.exact.prevent="onModalYT(article.youtubeId)" />
          </div>
          <div class="pt-10 pb-20 px-8 bg-white overflow-hidden md:rounded-b md:px-20">

            <div class="flex items-center py-4 px-6 bg-gray-200 rounded-lg">
              <div class="flex-shrink-0 w-16 mr-6">
                <img class="mx-auto" src="~/assets/img/case-study/Illustration-problem.png" srcset="~/assets/img/case-study/Illustration-problem@2x.png 2x, ~/assets/img/case-study/Illustration-problem@3x.png 3x" alt="">
              </div>
              <div class="flex-grow">
                <div class="inline-block py-1 px-2 rounded-sm bg-orange text-white text-sm leading-none">課題</div>
                <div class="mt-2 text-xs md:text-sm">{{article.problem}}</div>
              </div>
            </div>
            <div class="w-16 ml-6 -my-1 text-center">
              <img class="mx-auto" src="~/assets/img/case-study/arrow-down.svg" alt="">
            </div>
            <div class="flex items-center py-4 px-6 bg-gray-200 rounded-lg">
              <div class="flex-shrink-0 w-16 mr-6">
                <img class="mx-auto" src="~/assets/img/case-study/Illustration-solution.png" srcset="~/assets/img/case-study/Illustration-solution@2x.png 2x, ~/assets/img/case-study/Illustration-solution@3x.png 3x" alt="">
              </div>
              <div class="flex-grow">
                <div class="inline-block py-1 px-2 rounded-sm bg-secondary text-white text-sm leading-none">解決</div>
                <div class="mt-2 text-xs md:text-sm">{{article.solution}}</div>
              </div>
            </div>

            <div v-if="isContentsIndex" class="mt-16 mb-12">
              <p>
                <span class="inline-block py-1 px-2 rounded-sm bg-gray-800 text-white text-sm leading-none">目次</span>
              </p>
              <ul class="mt-4 content-table">
                <li v-for="(content, index) in article.contents" :key="index">
                  <span v-if="content.addToIndex" :class="content.headingLevel">
                    <nuxt-link v-scroll-to="`#section-${index + 1}`" to>{{content.shortTitle}}</nuxt-link>
                  </span>
                </li>
              </ul>
            </div>

            <div class="sections">
              <section class="section" :id="`section-${index + 1}`" v-for="(content, index) in article.contents" :key="index">
                <template v-if="content.enableHeading">
                  <h2 v-if="content.headingLevel == 'h2'">{{content.title}}</h2>
                  <h3 v-else>{{content.title}}</h3>
                </template>
                <md v-if="showBody(content)" :md="convertH4toEm(content.body)" />
              </section>
            </div>

            <hr class="border-gray-400 mt-20 mb-12">

            <div class="-mx-10 md:flex">
              <adjacent-article direction="prev" class="mr-auto" v-if="prevArticle" :article="prevArticle" />
              <adjacent-article direction="next" class="ml-auto" v-if="nextArticle" :article="nextArticle" />
            </div>
          </div>
        </contents-container>
      </div>
    </div>

    <ModalYouTube :show="showModalYt" :videoid="modalYtId" @close="onCloseModalYT" />
    <BreadcrumbsFoot :list="breadcrumbs" />
  </div>
</template>

<style lang="sass" scoped>
.thumbnail
  @apply bg-black bg-cover bg-center bg-no-repeat relative overflow-hidden
  padding-bottom: 50%
@import ~/assets/css/article.sass
</style>

<script>
import mixin from '@/components/blog/article/mixin'
import AdjacentArticle from '@/components/case-study/adjacentArticle.vue'
import ModalYouTube from "@/components/modalYouTube";
import ModalYouTubeMixin from "@/components/mixins/modalYouTube";
import BreadcrumbsFoot from '@/components/breadcrumbsFoot';
import Jsonld from "@/components/mixins/jsonld";

export default {
  mixins: [ mixin, ModalYouTubeMixin, Jsonld ],
  components: {
    AdjacentArticle,
    ModalYouTube,
    BreadcrumbsFoot
  },
  async middleware({ $strapi, params, redirect }) {
    if(Number.isInteger(Number(params.slug))) {
      const article = await $strapi.$http.$get(`case-studies?id=${params.slug}`);
      if(article.count > 0) {
        return redirect(`/case-study/${article.data[0].slug}`)
      }
    };
  },
  async asyncData({ $strapi, params, error, store, app }) {
    let article, articleCount, prevArticle, nextArticle
    const global = store.getters["cms/global"] || {}
    try {
      const casestudy = await $strapi.findOne("case-studies", params.slug)
      article = casestudy.data
      articleCount = casestudy.count
      prevArticle = casestudy.prev
      nextArticle = casestudy.next

      if (!article) {
        throw new Error('Not found')
      }
    } catch (e) {
      error({
        statusCode: 404,
        message: e.message
      })
    }
    return {
      global,
      article,
      articleCount,
      prevArticle,
      nextArticle,
      breadcrumbs: [
        { url: '/case-study', name: app.i18n.t('nav.casestudy') },
        { name: article ? article.title : '' },
      ],
    };
  },
  computed: {
    isContentsIndex() {
      const { article } = this
      return article && article.contents && article.contents.some(e => e.addToIndex)
    }
  },
  methods: {
    showBody (content) {
      return content && content.enableBody && content.body
    },
    convertH4toEm(md) {
      return md.replace(/(\n?)####\s+(.+)(\n)/g, '$1*$2*$3')
    }
  },
  head() {
    const { article } = this;
    const defaultSeo = this.global ? this.global.defaultSeo : {}
    const keywords = (article.keywords && article.keywords.substr(-1) === ',') ? article.keywords.slice(0, -1) : article.keywords
    const fullSeo = {
      ...defaultSeo,
      metaTitle: article.title,
      metaDescription: article.description,
      metaKeywords: `${keywords ? keywords.replace(/\s+/g, '') : this.$t('nav.casestudy')},${this.$t('keywords')}`
    };
    if (article.thumbnail) {
      fullSeo.shareImage = article.thumbnail
    }
    return {
      title: fullSeo.metaTitle,
      meta: this.$options.filters.cmsMetaTags(fullSeo)
    };
  },
  jsonld() {
    const ret = []
    const breadcrumbs = this.getBreadcrumbsJsonld(this.breadcrumbs)
    breadcrumbs && ret.push(breadcrumbs)
    return ret
  },
};
</script>
